import _ from 'lodash'
import React, { useCallback, useEffect, useState } from 'react'
import { Button } from 'react-bootstrap'
import ReactSelect, { Options } from 'react-select'

import { DashboardContext } from 'live/context/dashboard'
import globalNotification from 'live/services/global-notification'
import i18n from 'live/services/i18n'

import { listAssets } from 'plugin-codaf-risk-ext-bowtie/services'
import { OverviewConfigProps, overviewDisplayAPI } from 'plugin-codaf-risk-ext-bowtie/shared/api'
import { zoomLevel0, zoomLevel1, zoomLevel2, changeZoomLevel } from 'plugin-codaf-risk-ext-bowtie/utils/constants'

import { BowtieManagementWidgetConfigs, IAsset } from '../types'

interface Props extends Omit<React.ComponentProps<LiveWidgetConfigForm>, 'config' | 'widgetConfig'> {
    config: LiveWidgetConfig<BowtieManagementWidgetConfigs>
    widgetConfig: Readonly<BowtieManagementWidgetConfigs>
}

export function BowtieManagementConfigs({ config, onChange, onChangeLayoutConfig, widgetConfig }: Props): JSX.Element {
    const dashboardContext = React.useContext(DashboardContext)
    const [assets, setAssets] = React.useState<IAsset[]>([])
    const [selectedAssets, setSelectedAssets] = React.useState<number[]>(config.assets ?? [])
    // const [customZoomLevel0, setCustomZoomLevel0] = useState(zoomLevel0)
    // const [customZoomLevel1, setCustomZoomLevel1] = useState(zoomLevel1)
    // const [customZoomLevel2, setCustomZoomLevel2] = useState(zoomLevel2)
    const [minLevel1, setMinLevel1] = useState<number>(config?.customZoomLevels?.level1[0])
    const [maxLevel1, setMaxLevel1] = useState<number>(config?.customZoomLevels?.level1[1])

    // const debounceMaxFn = useCallback(_.debounce(handleDebounceMaxFn, 100, { leading: true }), [])

    // function handleDebounceMaxFn(inputValue: number) {
    //     setMaxLevel1(inputValue)
    // }

    function handleZoomLevelUpdate(): void {
        const level0Max = Number.parseFloat((minLevel1 - 0.01).toFixed(2))
        const level2Min = Number.parseFloat((maxLevel1 + 0.01).toFixed(2))
        const level0 = [0, level0Max]
        const level1 = [minLevel1, maxLevel1]
        const level2 = [level2Min, 999]
        onChangeLayoutConfig('customZoomLevels', { level0: level0, level1: level1, level2: level2 })
        // changeZoomLevel(level0, level1, level2)
    }

    useEffect(() => {
        if (minLevel1 && maxLevel1) {
            if (minLevel1 !== zoomLevel1[0] || maxLevel1 !== zoomLevel1[1]) {
                handleZoomLevelUpdate()
            }
        }
    }, [minLevel1, maxLevel1])

    const handleChangeMaxValue = (e: React.ChangeEvent<HTMLInputElement>): void => {
        e.preventDefault()
        setMaxLevel1(e.target.valueAsNumber)
    }
    const handleChangeMinValue = (e: React.ChangeEvent<HTMLInputElement>): void => {
        e.preventDefault()
        setMinLevel1(e.target.valueAsNumber)
    }

    function ExtendedEditor(props: OverviewConfigProps): JSX.Element {
        const currentEditor = overviewDisplayAPI.getDefaultOverview()
        const Editor = currentEditor?.config

        if (Editor && React.isValidElement(<Editor {...props} />)) {
            const showHR = !(overviewDisplayAPI.getDefaultOverview().hideDefaultResourcesToDisplaySelect === true)

            return (
                <>
                    {showHR && <hr style={{ marginTop: 8, marginBottom: 8, borderTopColor: 'var(--gray-300)' }} />}
                    <Editor {...props} />
                </>
            )
        }

        return <></>
    }

    React.useEffect(() => {
        setSelectedAssets(config.assets)
    }, [config.assets])

    return (
        <>
            {overviewDisplayAPI.getDefaultOverview()?.hideDefaultResourcesToDisplaySelect === false && (
                <>
                    <label>{i18n('Resources to display')}</label>
                    <ReactSelect
                        value={selectedAssets ?? []}
                        options={assets.map(asset => ({ value: asset.id, label: asset.name }))}
                        multi={true}
                        onChange={(e: Options<number>) => setSelectedAssets(e.map(opt => opt.value))}
                        searchable={true}
                        placeholder={i18n('Select your resources here...')}
                        clearable={false}
                        required={true}
                    />
                    <Button
                        onClick={() => onChange('assets', selectedAssets)}
                        bsStyle={selectedAssets !== config.assets && selectedAssets.length > 0 ? 'primary' : ''}
                        disabled={selectedAssets === config.assets || selectedAssets.length === 0}
                        style={{ marginTop: 8 }}
                    >
                        {i18n('Apply')}
                    </Button>
                </>
            )}

            <div>Custom Zoom level ranges</div>
            <div>level0</div>
            <div style={{ display: 'flex', flexDirection: 'row' }}>
                <div style={{ display: 'flex', flexDirection: 'column', margin: 4 }}>
                    <div>min</div>
                    <input
                        className="input"
                        type="number"
                        value={0}
                        readOnly={true}
                        disabled={true}
                        style={{ margin: 4 }}
                    />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', margin: 4 }}>
                    <div>max</div>
                    <input
                        className="input"
                        type="number"
                        value={(minLevel1 - 0.01).toFixed(2)}
                        step={0.01}
                        readOnly={true}
                        disabled={true}
                        style={{ margin: 4 }}
                    />
                </div>
            </div>
            <div>level1</div>
            <div style={{ display: 'flex', flexDirection: 'row' }}>
                <div style={{ display: 'flex', flexDirection: 'column', margin: 4 }}>
                    <div>min</div>
                    <input
                        key={'minlevel1'}
                        className="minlevel1"
                        type="number"
                        step={0.01}
                        value={minLevel1}
                        max={maxLevel1}
                        onChange={handleChangeMinValue}
                    />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', margin: 4 }}>
                    <div>max</div>
                    <input
                        key={'maxlevel1'}
                        className="maxlevel1"
                        type="number"
                        step={0.01}
                        value={maxLevel1}
                        min={minLevel1}
                        onChange={handleChangeMaxValue}
                    />
                </div>
            </div>
            <div>level2</div>
            <div style={{ display: 'flex', flexDirection: 'row' }}>
                <div style={{ display: 'flex', flexDirection: 'column', margin: 4 }}>
                    <div>min</div>
                    <input
                        className="input"
                        type="number"
                        value={(maxLevel1 + 0.01).toFixed(2)}
                        readOnly={true}
                        disabled={true}
                    />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', margin: 4 }}>
                    <div>max</div>
                    <input className="input" type="number" value={999} readOnly={true} disabled={true} />
                </div>
            </div>

            <ExtendedEditor
                config={config}
                onChange={onChange}
                assets={assets}
                onChangeLayoutConfig={onChangeLayoutConfig}
                widgetConfig={widgetConfig}
            />
        </>
    )
}
